import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { sizeLabel } from "@/lib/sizes";

const STATUSES = ["all", "draft", "requested", "accepted", "picked_up", "in_transit", "delivered", "cancelled"];

const statusBadge = (s) => {
  const map = {
    draft: "bg-slate-100 text-slate-600",
    requested: "bg-amber-100 text-amber-700",
    accepted: "bg-blue-100 text-blue-700",
    picked_up: "bg-blue-100 text-blue-700",
    in_transit: "bg-violet-100 text-violet-700",
    delivered: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700",
  };
  return <Badge className={`text-[10px] ${map[s] || "bg-slate-100 text-slate-600"}`}>{(s || "").toUpperCase()}</Badge>;
};

const Parcels = () => {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [status, setStatus] = useState(sp.get("status") || "all");
  const [date_from, setFrom] = useState(sp.get("date_from") || "");
  const [date_to, setTo] = useState(sp.get("date_to") || "");
  const [origin_city, setOrigin] = useState(sp.get("origin_city") || "");
  const [destination_city, setDest] = useState(sp.get("destination_city") || "");
  const [carrier_id, setCarrier] = useState(sp.get("carrier_id") || "");
  const [sender_id, setSender] = useState(sp.get("sender_id") || "");
  const [sort, setSort] = useState(sp.get("sort") || "-created_at");
  const [page, setPage] = useState(parseInt(sp.get("page") || "1", 10));

  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q, status, date_from, date_to, origin_city, destination_city, carrier_id, sender_id, sort, page, page_size: 50 };
      Object.keys(params).forEach(k => (params[k] === "" || params[k] === "all") && delete params[k]);
      const r = await adminApi.get("/parcels", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load parcels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const next = { q, status, date_from, date_to, origin_city, destination_city, carrier_id, sender_id, sort, page };
    Object.keys(next).forEach(k => (next[k] === "" || next[k] === "all") && delete next[k]);
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, date_from, date_to, origin_city, destination_city, carrier_id, sender_id, sort, page]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 50))), [data]);
  const toggleSort = (field) => setSort(sort === field ? "-" + field : field);

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Parcels & deliveries</h1>
          <p className="text-sm text-slate-500 mt-1">Every parcel ever posted, with the full price breakdown.</p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Search</label>
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400"/>
            <Input data-testid="parcels-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Parcel ID or phone" className="pl-8"/>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Status</label>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger data-testid="filter-status" className="mt-1 w-[150px]"><SelectValue/></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s === "all" ? "All" : s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">From</label>
          <Input data-testid="filter-from" type="date" value={date_from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="mt-1"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">To</label>
          <Input data-testid="filter-to" type="date" value={date_to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="mt-1"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Origin city</label>
          <Input data-testid="filter-origin" value={origin_city} onChange={(e) => { setOrigin(e.target.value); setPage(1); }} placeholder="e.g. Bangalore" className="mt-1 w-[140px]"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Destination</label>
          <Input data-testid="filter-destination" value={destination_city} onChange={(e) => { setDest(e.target.value); setPage(1); }} placeholder="e.g. Hyderabad" className="mt-1 w-[140px]"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Carrier ID</label>
          <Input data-testid="filter-carrier" value={carrier_id} onChange={(e) => { setCarrier(e.target.value); setPage(1); }} placeholder="uid" className="mt-1 w-[130px] font-mono text-xs"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Sender ID</label>
          <Input data-testid="filter-sender" value={sender_id} onChange={(e) => { setSender(e.target.value); setPage(1); }} placeholder="uid" className="mt-1 w-[130px] font-mono text-xs"/>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100 flex items-center justify-between">
          <span data-testid="parcels-total">{data.total} parcels</span>
          <span>Page {page} of {totalPages}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Parcel</th>
                <Th onClick={() => toggleSort("created_at")} active={sort.replace(/^-/, "") === "created_at"}>Posted</Th>
                <th className="px-4 py-2 text-left">Sender</th>
                <th className="px-4 py-2 text-left">Carrier</th>
                <th className="px-4 py-2 text-left">Route</th>
                <Th onClick={() => toggleSort("status")} active={sort.replace(/^-/, "") === "status"}>Status</Th>
                <th className="px-4 py-2 text-right">Sender ₹</th>
                <th className="px-4 py-2 text-right">Commission ₹</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map(p => (
                <tr key={p.id} data-testid={`parcel-row-${p.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/admin/parcels/${p.id}`} className="text-slate-900 font-mono text-xs hover:underline">{p.id.slice(0, 8)}</Link>
                    <div className="text-[10px] text-slate-400 uppercase">{sizeLabel(p)} · {p.category || "general"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs tabular-nums text-slate-600">{p.created_at ? new Date(p.created_at).toLocaleString() : ""}</td>
                  <td className="px-4 py-3 text-xs">{(p.sender || {}).name || "—"}<div className="text-slate-400">{(p.sender || {}).phone}</div></td>
                  <td className="px-4 py-3 text-xs">{(p.carrier || {}).name || <span className="text-slate-400 italic">unassigned</span>}<div className="text-slate-400">{(p.carrier || {}).phone}</div></td>
                  <td className="px-4 py-3 text-xs">{p.pickup_location} → {p.drop_location}</td>
                  <td className="px-4 py-3">{statusBadge(p.status)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{p.sender_total ? `₹${p.sender_total.toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500">{p.commission ? `₹${p.commission.toFixed(2)}` : "—"}</td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-400">No parcels match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="pg-prev">← Prev</Button>
        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} data-testid="pg-next">Next →</Button>
      </div>
    </div>
  );
};

const Th = ({ children, onClick, active }) => (
  <th onClick={onClick} className="px-4 py-2 text-left cursor-pointer select-none hover:text-slate-900">
    {children} {active && <span className="text-slate-400 text-[9px]">▼</span>}
  </th>
);

export default Parcels;
